import React, { useEffect, useMemo, useState } from 'react'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { ToastContainer, toast } from 'react-toastify'
import moment from 'moment'
import { getGeminiPricing, saveGeminiPricing } from '../../../repository/aiCostApi'
import {
  GEMINI_DEFAULT_MODELS, GEMINI_PRICING_SOURCE, GEMINI_PRICING_FETCHED_AT,
  PRICE_KINDS, PRICE_MODES, PRICE_CONDITIONS, PRICE_UNITS, MODEL_CATEGORIES, MODEL_STATUSES,
  effectivePrice, estimateCost,
} from '../../../components/AICost/geminiPricingDefaults'
import { usd, num, dateTime } from '../../../components/AICost/aiCostFormat'
import '../../../components/AICost/aiCost.css'

const labelOf = (list, value) => list.find((o) => o.value === value)?.label || value
const today = () => moment().format('YYYY-MM-DD')
const clone = (v) => JSON.parse(JSON.stringify(v))
const emptyRow = () => ({ kind: 'input', modality: 'text', mode: 'standard', price: '', unit: 'tokens', condition: 'all', nextPrice: null, nextPriceFrom: null, note: '' })
const emptyModel = () => ({ modelId: '', name: '', category: 'text', freeTier: false, status: 'active', note: '', prices: [emptyRow()] })

const unitSuffix = (unit) => (unit === 'tokens' ? '' : ` ${labelOf(PRICE_UNITS, unit).replace('per ', '/ ')}`)

/** One table cell: every price line of a kind + mode, with tier and upcoming change */
const PriceCell = ({ rows, date }) => {
  if (!rows.length) return <span className="text-muted">-</span>
  return rows.map((r, i) => (
    <div key={i} className="ai-pricing-line">
      <strong>{usd(effectivePrice(r, date), 4)}</strong>
      {unitSuffix(r.unit)}
      {(r.modality !== 'all' && r.modality !== 'text') && <span className="text-muted"> · {r.modality}</span>}
      {r.condition !== 'all' && <span className="text-muted"> · {r.condition === 'gt200k' ? '>200k' : '≤200k'}</span>}
      {r.nextPrice != null && r.nextPriceFrom && date < r.nextPriceFrom && (
        <div className="ai-pricing-next">→ {usd(r.nextPrice, 4)} from {moment(r.nextPriceFrom).format('DD MMM YYYY')}</div>
      )}
      {r.note && <div className="ai-pricing-next">{r.note}</div>}
    </div>
  ))
}

const COLUMNS = [
  { label: 'Input', kind: 'input', mode: 'standard' },
  { label: 'Output', kind: 'output', mode: 'standard' },
  { label: 'Cached input', kind: 'cache', mode: 'standard' },
  { label: 'Batch input', kind: 'input', mode: 'batch' },
  { label: 'Batch output', kind: 'output', mode: 'batch' },
]

const validate = (draft, models, editIndex) => {
  const id = draft.modelId.trim()
  if (!id) return 'Model ID is required'
  if (models.some((m, i) => i !== editIndex && m.modelId === id)) return `${id} already exists`
  if (!draft.name.trim()) return 'Display name is required'
  if (!draft.prices.length) return 'Add at least one price'
  for (const [i, r] of draft.prices.entries()) {
    if (r.price === '' || Number.isNaN(Number(r.price)) || Number(r.price) < 0) return `Price line ${i + 1}: enter a price of 0 or more`
    const hasNext = r.nextPrice !== null && r.nextPrice !== ''
    if (hasNext && (Number.isNaN(Number(r.nextPrice)) || Number(r.nextPrice) < 0)) return `Price line ${i + 1}: new price must be 0 or more`
    if (hasNext !== Boolean(r.nextPriceFrom)) return `Price line ${i + 1}: set both the new price and its start date, or neither`
  }
  return null
}

/** Normalises form strings back to numbers / nulls before saving */
const normalise = (draft) => ({
  ...draft,
  modelId: draft.modelId.trim(),
  name: draft.name.trim(),
  note: draft.note.trim(),
  prices: draft.prices.map((r) => {
    const hasNext = r.nextPrice !== null && r.nextPrice !== ''
    return {
      ...r,
      modality: r.modality.trim() || 'all',
      price: Number(r.price),
      nextPrice: hasNext ? Number(r.nextPrice) : null,
      nextPriceFrom: hasNext ? r.nextPriceFrom : null,
      note: (r.note || '').trim(),
    }
  }),
})

const ModelDialog = ({ open, initial, onClose, onSave, models, editIndex }) => {
  const [draft, setDraft] = useState(initial)
  const [error, setError] = useState(null)

  useEffect(() => { setDraft(initial); setError(null) }, [initial])

  if (!draft) return null
  const set = (field, value) => setDraft((d) => ({ ...d, [field]: value }))
  const setRow = (i, field, value) =>
    setDraft((d) => ({ ...d, prices: d.prices.map((r, j) => (j === i ? { ...r, [field]: value } : r)) }))

  const submit = () => {
    const message = validate(draft, models, editIndex)
    if (message) return setError(message)
    onSave(normalise(draft))
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{editIndex == null ? 'Add Gemini model' : `Edit ${initial?.name}`}</DialogTitle>
      <DialogContent dividers>
        <div className="row g-3">
          <label className="col-md-4">
            <span className="form-label small fw-semibold">Model ID</span>
            <input className="form-control ai-cost-mono" placeholder="gemini-3.8-flash" value={draft.modelId} onChange={(e) => set('modelId', e.target.value)} />
          </label>
          <label className="col-md-4">
            <span className="form-label small fw-semibold">Display name</span>
            <input className="form-control" placeholder="Gemini 3.8 Flash" value={draft.name} onChange={(e) => set('name', e.target.value)} />
          </label>
          <label className="col-md-2">
            <span className="form-label small fw-semibold">Type</span>
            <select className="form-select" value={draft.category} onChange={(e) => set('category', e.target.value)}>
              {MODEL_CATEGORIES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label className="col-md-2">
            <span className="form-label small fw-semibold">Status</span>
            <select className="form-select" value={draft.status} onChange={(e) => set('status', e.target.value)}>
              {MODEL_STATUSES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label className="col-md-9">
            <span className="form-label small fw-semibold">Note</span>
            <input className="form-control" placeholder="e.g. Retiring October 2, 2026" value={draft.note} onChange={(e) => set('note', e.target.value)} />
          </label>
          <label className="col-md-3 d-flex align-items-end gap-2 pb-2">
            <input type="checkbox" className="form-check-input" checked={draft.freeTier} onChange={(e) => set('freeTier', e.target.checked)} />
            <span className="small">Free tier available</span>
          </label>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-4 mb-2">
          <strong>Prices (USD)</strong>
          <Button size="small" onClick={() => set('prices', [...draft.prices, emptyRow()])}><AddIcon fontSize="small" /> Add price</Button>
        </div>
        <div className="table-responsive">
          <table className="table ai-cost-table ai-pricing-edit">
            <thead>
              <tr>
                <th>Type</th><th>Mode</th><th>Modality</th><th>Prompt size</th><th>Price</th><th>Unit</th>
                <th>New price</th><th>From</th><th>Note</th><th />
              </tr>
            </thead>
            <tbody>
              {draft.prices.map((r, i) => (
                <tr key={i}>
                  <td>
                    <select className="form-select form-select-sm" value={r.kind} onChange={(e) => setRow(i, 'kind', e.target.value)}>
                      {PRICE_KINDS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </td>
                  <td>
                    <select className="form-select form-select-sm" value={r.mode} onChange={(e) => setRow(i, 'mode', e.target.value)}>
                      {PRICE_MODES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </td>
                  <td><input className="form-control form-control-sm" placeholder="text, audio, all" value={r.modality} onChange={(e) => setRow(i, 'modality', e.target.value)} /></td>
                  <td>
                    <select className="form-select form-select-sm" value={r.condition} onChange={(e) => setRow(i, 'condition', e.target.value)}>
                      {PRICE_CONDITIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </td>
                  <td><input type="number" min="0" step="any" className="form-control form-control-sm ai-pricing-num" value={r.price} onChange={(e) => setRow(i, 'price', e.target.value)} /></td>
                  <td>
                    <select className="form-select form-select-sm" value={r.unit} onChange={(e) => setRow(i, 'unit', e.target.value)}>
                      {PRICE_UNITS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </td>
                  <td><input type="number" min="0" step="any" className="form-control form-control-sm ai-pricing-num" value={r.nextPrice ?? ''} onChange={(e) => setRow(i, 'nextPrice', e.target.value)} /></td>
                  <td><input type="date" className="form-control form-control-sm" value={r.nextPriceFrom || ''} onChange={(e) => setRow(i, 'nextPriceFrom', e.target.value || null)} /></td>
                  <td><input className="form-control form-control-sm" value={r.note || ''} onChange={(e) => setRow(i, 'note', e.target.value)} /></td>
                  <td>
                    <IconButton size="small" aria-label="Remove price" onClick={() => set('prices', draft.prices.filter((_, j) => j !== i))}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-muted small">"New price" and "From" record an announced change; the new price is used automatically from that date.</div>
        {error && <div className="alert alert-danger py-2 mt-3 mb-0">{error}</div>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit}>Done</Button>
      </DialogActions>
    </Dialog>
  )
}

const Calculator = ({ models }) => {
  const tokenModels = models.filter((m) => m.prices.some((r) => r.unit === 'tokens' && r.kind !== 'storage'))
  const [modelId, setModelId] = useState('gemini-3.8-flash')
  const [form, setForm] = useState({ inputTokens: 1000, outputTokens: 500, cachedTokens: 0, mode: 'standard', modality: 'text', requests: 1000, date: today() })
  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }))

  const model = tokenModels.find((m) => m.modelId === modelId) || tokenModels[0]
  const est = model && estimateCost(model, {
    ...form,
    inputTokens: Number(form.inputTokens) || 0,
    outputTokens: Number(form.outputTokens) || 0,
    cachedTokens: Number(form.cachedTokens) || 0,
  })
  const requests = Number(form.requests) || 0

  const line = (label, l) => (
    <tr>
      <td>{label}</td>
      <td className="text-end">{num(l.tokens)}</td>
      <td className="text-end">{l.rate == null ? <span className="text-muted">no price</span> : `${usd(l.rate, 4)} / 1M`}</td>
      <td className="text-end">{l.cost == null ? '-' : usd(l.cost)}</td>
    </tr>
  )

  return (
    <div className="ai-cost-card">
      <div className="ai-cost-card-head">
        <div>
          <div className="ai-cost-card-title">Token cost calculator</div>
          <div className="ai-cost-card-subtitle">Cost of one request from its token counts, using the prices below (including any unsaved edits)</div>
        </div>
      </div>
      <div className="ai-cost-filters ai-pricing-calc">
        <label className="ai-cost-filter">
          <span>Model</span>
          <select className="form-select" value={model?.modelId || ''} onChange={(e) => setModelId(e.target.value)}>
            {tokenModels.map((m) => <option key={m.modelId} value={m.modelId}>{m.name}</option>)}
          </select>
        </label>
        <label className="ai-cost-filter">
          <span>Mode</span>
          <select className="form-select" value={form.mode} onChange={(e) => set('mode', e.target.value)}>
            {PRICE_MODES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label className="ai-cost-filter">
          <span>Input type</span>
          <select className="form-select" value={form.modality} onChange={(e) => set('modality', e.target.value)}>
            {['text', 'image', 'video', 'audio'].map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </label>
        {[['inputTokens', 'Input tokens'], ['cachedTokens', 'Cached input tokens'], ['outputTokens', 'Output tokens'], ['requests', 'Requests']].map(([field, label]) => (
          <label key={field} className="ai-cost-filter">
            <span>{label}</span>
            <input type="number" min="0" className="form-control ai-pricing-num" value={form[field]} onChange={(e) => set(field, e.target.value)} />
          </label>
        ))}
        <label className="ai-cost-filter">
          <span>Price date</span>
          <input type="date" className="form-control" value={form.date} onChange={(e) => set('date', e.target.value || today())} />
        </label>
      </div>
      {est && (
        <div className="table-responsive">
          <table className="table ai-cost-table">
            <thead>
              <tr><th>Line</th><th className="text-end">Tokens</th><th className="text-end">Rate</th><th className="text-end">Cost / request</th></tr>
            </thead>
            <tbody>
              {line('Input', est.input)}
              {Number(form.cachedTokens) > 0 && line('Cached input', est.cache)}
              {line('Output (incl. thinking)', est.output)}
              <tr className="fw-semibold">
                <td colSpan={3}>Total per request {est.tier === 'gt200k' && <span className="text-muted fw-normal">(prompt &gt; 200k tier)</span>}</td>
                <td className="text-end">{usd(est.total)}</td>
              </tr>
              <tr className="fw-semibold">
                <td colSpan={3}>Total for {num(requests)} requests</td>
                <td className="text-end">{usd(est.total * requests)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

/** Settings > Gemini Pricing: editable per-model Gemini API prices, saved in the backend */
const GeminiPricing = () => {
  const [models, setModels] = useState([])
  const [meta, setMeta] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('')
  const [dialog, setDialog] = useState({ open: false, index: null, initial: null })
  const date = today()

  const load = async () => {
    setLoading(true)
    try {
      const res = await getGeminiPricing()
      const saved = res.data?.models || []
      setMeta({ updatedAt: res.data?.updatedAt, updatedBy: res.data?.updatedBy, saved: saved.length > 0 })
      setModels(saved.length ? saved : clone(GEMINI_DEFAULT_MODELS))
      setDirty(!saved.length)
    } catch (err) {
      toast.error(err.message)
      setMeta({ saved: false, loadFailed: true })
      setModels(clone(GEMINI_DEFAULT_MODELS))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const change = (next) => { setModels(next); setDirty(true) }

  const save = async () => {
    setSaving(true)
    try {
      const res = await saveGeminiPricing({ models })
      toast.success(res.message || 'Gemini pricing saved')
      setMeta({ updatedAt: res.data?.updatedAt || new Date().toISOString(), updatedBy: res.data?.updatedBy, saved: true })
      setDirty(false)
    } catch (err) {
      toast.error(err.message)
    } finally {
      setSaving(false)
    }
  }

  const resetToGoogle = () => {
    if (!window.confirm(`Replace the list with Google's prices from ${moment(GEMINI_PRICING_FETCHED_AT).format('DD MMM YYYY')}? Your edits stay unsaved until you click Save.`)) return
    change(clone(GEMINI_DEFAULT_MODELS))
  }

  const remove = (index) => {
    if (!window.confirm(`Remove ${models[index].name}?`)) return
    change(models.filter((_, i) => i !== index))
  }

  const saveModel = (model) => {
    change(dialog.index == null ? [...models, model] : models.map((m, i) => (i === dialog.index ? model : m)))
    setDialog({ open: false, index: null, initial: null })
  }

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    return models
      .map((m, index) => ({ m, index }))
      .filter(({ m }) => (!category || m.category === category) &&
        (!q || m.name.toLowerCase().includes(q) || m.modelId.toLowerCase().includes(q)))
  }, [models, search, category])

  if (loading) return <div className="ai-cost-state">Loading Gemini pricing...</div>

  return (
    <div className="container-fluid py-3 ai-cost">
      <ToastContainer />
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h5 className="mb-1">Gemini Pricing</h5>
          <div className="text-muted small">
            Paid-tier prices in USD, per 1M tokens unless shown otherwise. Source:{' '}
            <a href={GEMINI_PRICING_SOURCE} target="_blank" rel="noreferrer">Google Gemini API pricing</a>
            {meta?.saved && meta.updatedAt && <> · Last saved {dateTime(meta.updatedAt)}{meta.updatedBy ? ` by ${meta.updatedBy}` : ''}</>}
          </div>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <Button variant="outlined" onClick={resetToGoogle}>Reset to Google prices</Button>
          <Button variant="outlined" onClick={() => setDialog({ open: true, index: null, initial: emptyModel() })}><AddIcon /> Add model</Button>
          <Button variant="contained" disabled={saving || !dirty} onClick={save}>{saving ? 'Saving...' : 'Save'}</Button>
        </div>
      </div>

      {!meta?.saved && (
        <div className="alert alert-warning py-2">
          {meta?.loadFailed ? 'Could not load saved prices. ' : 'No prices saved yet. '}
          Showing Google's prices from {moment(GEMINI_PRICING_FETCHED_AT).format('DD MMM YYYY')}. Click Save to store them.
        </div>
      )}
      {meta?.saved && dirty && <div className="alert alert-info py-2">You have unsaved changes.</div>}

      <Calculator models={models} />

      <div className="ai-cost-card">
        <div className="ai-cost-filters">
          <label className="ai-cost-filter">
            <span>Search</span>
            <input className="form-control" placeholder="Model name or ID" value={search} onChange={(e) => setSearch(e.target.value)} />
          </label>
          <label className="ai-cost-filter">
            <span>Type</span>
            <select className="form-select" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">All types</option>
              {MODEL_CATEGORIES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <span className="ai-cost-reset text-muted small">{visible.length} of {models.length} models</span>
        </div>

        {visible.length === 0 ? (
          <div className="ai-cost-state">No models match.</div>
        ) : (
          <div className="table-responsive">
            <table className="table ai-cost-table ai-pricing-table">
              <thead>
                <tr>
                  <th>Model</th>
                  {COLUMNS.map((c) => <th key={c.label}>{c.label}</th>)}
                  <th>Other</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visible.map(({ m, index }) => {
                  const shown = COLUMNS.flatMap((c) => m.prices.filter((r) => r.kind === c.kind && r.mode === c.mode))
                  const other = m.prices.filter((r) => !shown.includes(r))
                  return (
                    <tr key={m.modelId}>
                      <td>
                        <div className="fw-semibold">{m.name}</div>
                        <div className="ai-cost-mono text-muted">{m.modelId}</div>
                        <div className="d-flex flex-wrap gap-1 mt-1">
                          <span className="badge text-bg-light">{labelOf(MODEL_CATEGORIES, m.category)}</span>
                          {m.status !== 'active' && (
                            <span className={`badge ${m.status === 'deprecated' ? 'text-bg-danger' : 'text-bg-warning'}`}>{labelOf(MODEL_STATUSES, m.status)}</span>
                          )}
                          {m.freeTier && <span className="badge text-bg-success">Free tier</span>}
                        </div>
                        {m.note && <div className="ai-pricing-next">{m.note}</div>}
                      </td>
                      {COLUMNS.map((c) => (
                        <td key={c.label}>
                          <PriceCell rows={m.prices.filter((r) => r.kind === c.kind && r.mode === c.mode)} date={date} />
                        </td>
                      ))}
                      <td>
                        {other.length ? other.map((r, i) => (
                          <div key={i} className="ai-pricing-line">
                            <span className="text-muted">{labelOf(PRICE_KINDS, r.kind)}{r.mode === 'batch' ? ' (batch)' : ''}: </span>
                            <PriceCell rows={[r]} date={date} />
                          </div>
                        )) : <span className="text-muted">-</span>}
                      </td>
                      <td className="text-nowrap">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => setDialog({ open: true, index, initial: clone(m) })}><EditIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Remove">
                          <IconButton size="small" onClick={() => remove(index)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ModelDialog
        open={dialog.open}
        initial={dialog.initial}
        editIndex={dialog.index}
        models={models}
        onClose={() => setDialog({ open: false, index: null, initial: null })}
        onSave={saveModel}
      />
    </div>
  )
}

export default GeminiPricing
